import { formatCurrency, transformDateToLocalDateString } from "helpers";
import { formatUnitPrice } from "services/api/pricing/consts";
import { PricingOverrideState } from "services/api/pricing/overrideTypes";
import { Badge } from "ui/components/Badge";
import { Button } from "ui/components/Button/Button";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";

import { DeviationRow } from "../../types";

export const STATE_BADGE: Record<
  PricingOverrideState,
  { text: string; variant: "success" | "warning" | "danger" | "secondary" | "info" }
> = {
  Effective: { text: "Aktivno", variant: "success" },
  PendingApproval: { text: "Čeka odobrenje", variant: "warning" },
  Rejected: { text: "Odbijeno", variant: "danger" },
  Expired: { text: "Isteklo", variant: "secondary" },
  NotYetValid: { text: "Još ne vrijedi", variant: "info" },
};

const formatValue = (row: DeviationRow): string => {
  if (row.adjustmentType === "Absolute") {
    return formatUnitPrice.format(row.value);
  }

  const sign = row.value > 0 ? "+" : "";

  return `${sign}${row.value.toFixed(2).replace(".", ",")} %`;
};

/** „Istječe uskoro" se izvodi iz roka na dan prikaza, ne iz spremljenog stanja. */
const isExpiringSoon = (row: DeviationRow): boolean =>
  row.state === "Effective" && row.expiresInDays >= 0 && row.expiresInDays <= 30;

/** Radnje nad retkom idu kroz `onEventClick`, kao i drugdje u projektu. */
export enum DeviationEventType {
  APPROVE = "approve",
  REJECT = "reject",
  EDIT = "edit",
}

export const DEVIATION_COLUMNS: Column[] = [
  {
    title: "Klijent",
    field: "tenantName",
    sortable: true,
    type: ColumnTypes.string,
  },
  {
    title: "Ključ",
    field: "keyCode",
    sortable: true,
    type: ColumnTypes.custom,
    render: (row: DeviationRow) => (
      <span className="font-mono text-xs text-slate-600">{row.keyCode}</span>
    ),
  },
  {
    title: "Promjena",
    field: "value",
    sortable: true,
    type: ColumnTypes.custom,
    render: (row: DeviationRow) => (
      <div className="whitespace-nowrap">
        <span className="font-semibold text-slate-700">{formatValue(row)}</span>

        {row.adjustmentType === "Absolute" && (
          <div className="mt-1">
            <Badge text="Fiksno" variant="warning" height="small" />
          </div>
        )}
      </div>
    ),
  },
  {
    title: "Mjesečno",
    field: "monthlyEffect",
    sortable: true,
    type: ColumnTypes.custom,
    // Prazno znači da se količina ne mjeri; nula bi tvrdila da uvjet ništa ne stoji.
    render: (row: DeviationRow) =>
      row.monthlyEffect === null ? (
        <span className="text-slate-400" title="Količina se ne mjeri">
          —
        </span>
      ) : (
        <span className="font-semibold text-slate-700 whitespace-nowrap">
          {formatCurrency.format(row.monthlyEffect)}
        </span>
      ),
  },
  {
    title: "Razlog",
    field: "reason",
    type: ColumnTypes.custom,
    render: (row: DeviationRow) => (
      <span className="text-slate-500">{row.reason}</span>
    ),
  },
  {
    title: "Istječe",
    field: "validTo",
    sortable: true,
    type: ColumnTypes.custom,
    render: (row: DeviationRow) => (
      <div className="whitespace-nowrap">
        <span className="text-slate-600">
          {transformDateToLocalDateString(new Date(row.validTo))}
        </span>

        {isExpiringSoon(row) && (
          <div className="mt-1">
            <Badge
              text={`za ${row.expiresInDays} d`}
              variant="warning"
              height="small"
              showDot
            />
          </div>
        )}
      </div>
    ),
  },
  {
    title: "Stanje",
    field: "state",
    sortable: true,
    type: ColumnTypes.custom,
    render: (row: DeviationRow) => (
      <div>
        <Badge
          text={STATE_BADGE[row.state].text}
          variant={STATE_BADGE[row.state].variant}
          height="small"
          showDot
        />

        {row.rejectionReason && (
          <p className="text-xs text-slate-400 mt-1">{row.rejectionReason}</p>
        )}
      </div>
    ),
  },
  {
    title: "",
    field: "actions",
    type: ColumnTypes.custom,
    render: (row: DeviationRow, onEventClick: any) => (
      <div className="flex flex-wrap gap-2 justify-end">
        {row.status === "PendingApproval" && (
          <>
            <Button
              variant="primary"
              children="Odobri"
              onClick={() => onEventClick(row.id, DeviationEventType.APPROVE)}
            />
            <Button
              variant="outlined"
              children="Odbij"
              onClick={() => onEventClick(row.id, DeviationEventType.REJECT)}
            />
          </>
        )}

        <Button
          variant="outlined"
          children="Uredi"
          onClick={() => onEventClick(row.id, DeviationEventType.EDIT)}
        />
      </div>
    ),
  },
];
